import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { db, getSetting, setSetting } from '../../db/shiksha-db';
import { useAuth } from '../../hooks/useAuth';

/**
 * AdminSettings — Configuration page for school name, sheet links, and academic year.
 */
export function AdminSettings() {
  const { getIdToken } = useAuth();

  const [schoolName, setSchoolName] = useState('');
  const [masterSheetLink, setMasterSheetLink] = useState('');
  const [sessionSheetLink, setSessionSheetLink] = useState('');
  const [academicYear, setAcademicYear] = useState('2026-27');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Load existing settings
  useEffect(() => {
    const loadSettings = async () => {
      const name = await getSetting('schoolName');
      const master = await getSetting('masterSheetLink');
      const session = await getSetting('sessionSheetLink');
      const year = await getSetting('academicYear');
      if (name) setSchoolName(name);
      if (master) setMasterSheetLink(master);
      if (session) setSessionSheetLink(session);
      if (year) setAcademicYear(year);
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSaved(false);

    try {
      // Save to local IndexedDB
      await setSetting('schoolName', schoolName);
      await setSetting('masterSheetLink', masterSheetLink);
      await setSetting('sessionSheetLink', sessionSheetLink);
      await setSetting('academicYear', academicYear);

      // Also push to backend if online
      if (navigator.onLine) {
        try {
          const token = await getIdToken();
          if (token) {
            const workerUrl = import.meta.env.VITE_WORKER_API_URL || 'https://shiksha-edge-api.workers.dev';
            await fetch(`${workerUrl}/api/admin/settings`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                schoolName,
                masterSheetLink,
                sessionSheetLink,
                academicYear,
              }),
            });
          }
        } catch (err) {
          console.warn('Failed to sync settings to backend:', err);
          // Not fatal — local settings are saved
        }
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper pb-8">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            className="p-2 rounded-lg bg-white border border-divider hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={20} className="text-ink" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-ink">System Settings</h1>
            <p className="text-sm text-graphite font-secondary">Configure school details, sheet links, and academic year.</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* School Information */}
          <div className="bg-white border border-divider rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-ink mb-4">School Information</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="school-name" className="block text-sm font-medium text-ink mb-1">
                  School Name
                </label>
                <input
                  id="school-name"
                  type="text"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="e.g. RSBRTL Bhuratali"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors"
                />
                <p className="text-xs text-graphite mt-1 font-secondary">This name appears on report card headers.</p>
              </div>

              <div>
                <label htmlFor="academic-year" className="block text-sm font-medium text-ink mb-1">
                  Academic Year
                </label>
                <select
                  id="academic-year"
                  value={academicYear}
                  onChange={(e) => setAcademicYear(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
                >
                  <option value="2024-25">2024-25</option>
                  <option value="2025-26">2025-26</option>
                  <option value="2026-27">2026-27</option>
                  <option value="2027-28">2027-28</option>
                </select>
              </div>
            </div>
          </div>

          {/* Google Sheets Configuration */}
          <div className="bg-white border border-divider rounded-xl p-6 shadow-sm">
            <h2 className="text-lg font-bold text-ink mb-1">Google Sheets Configuration</h2>
            <p className="text-sm text-graphite mb-4 font-secondary">
              Link your Google Sheets for data sync. The backend Cloudflare Worker uses these to read/write student data.
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor="master-sheet" className="block text-sm font-medium text-ink mb-1">
                  Master Sheet Link
                </label>
                <div className="flex gap-2">
                  <input
                    id="master-sheet"
                    type="url"
                    value={masterSheetLink}
                    onChange={(e) => setMasterSheetLink(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors font-mono"
                  />
                  {masterSheetLink && (
                    <a
                      href={masterSheetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <ExternalLink size={16} className="text-graphite" />
                    </a>
                  )}
                </div>
                <p className="text-xs text-graphite mt-1 font-secondary">The main sheet with tabs: Students, Attendance, Marks, Config.</p>
              </div>

              <div>
                <label htmlFor="session-sheet" className="block text-sm font-medium text-ink mb-1">
                  Academic Session Sheet Link
                </label>
                <div className="flex gap-2">
                  <input
                    id="session-sheet"
                    type="url"
                    value={sessionSheetLink}
                    onChange={(e) => setSessionSheetLink(e.target.value)}
                    placeholder="https://docs.google.com/spreadsheets/d/..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-colors font-mono"
                  />
                  {sessionSheetLink && (
                    <a
                      href={sessionSheetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                    >
                      <ExternalLink size={16} className="text-graphite" />
                    </a>
                  )}
                </div>
                <p className="text-xs text-graphite mt-1 font-secondary">Optional separate sheet for current session data.</p>
              </div>
            </div>
          </div>

          {/* Error/Success */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-6 py-3 font-semibold transition-colors shadow-sm disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : saved ? (
                <CheckCircle size={18} />
              ) : (
                <Save size={18} />
              )}
              {isSaving ? 'Saving...' : saved ? 'Saved!' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
